// Generic curriculum data layer.
//
// Operates on the unified `curriculum_libraries` collection:
//
//   curriculum_libraries/{libraryId}
//     categories/{categoryId}
//       textbooks/{textbookId}
//         resources/{resourceId}
//
// A "library descriptor" is `{ id, layoutType, storageRoot }` where
// `storageRoot` defaults to `Curriculum_Library/{id}`. `createCurriculumService(library)`
// returns an object whose methods are bound to that library so the UI never has
// to repeat the library id. The textbook/resource document shape is identical to
// the legacy AP/SAT/ACT model, so the existing detail UI ports almost verbatim.

import { db, storage } from '../../../firebaseConfig';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';

export const LIBRARIES_COLLECTION = 'curriculum_libraries';

export function storageRootForLibrary(libraryId) {
  return `Curriculum_Library/${libraryId}`;
}

function safeMillis(value) {
  return value?.toMillis?.() || 0;
}

export function sortByOrder(items) {
  return [...items].sort((a, b) => {
    const orderA = Number.isFinite(a.orderIndex) ? a.orderIndex : Number.MAX_SAFE_INTEGER;
    const orderB = Number.isFinite(b.orderIndex) ? b.orderIndex : Number.MAX_SAFE_INTEGER;

    if (orderA !== orderB) return orderA - orderB;

    const createdA = safeMillis(a.createdAt);
    const createdB = safeMillis(b.createdAt);
    if (createdA !== createdB) return createdA - createdB;

    return String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''));
  });
}

function sanitizeFileName(fileName = '') {
  return fileName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 120);
}

function makeStorageToken() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

async function safeDeleteStorage(storagePath) {
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (error) {
    // Ignore missing files to keep deletes resilient.
  }
}

async function uploadCurriculumFile(storagePath, file) {
  const snap = await uploadBytes(ref(storage, storagePath), file);
  const url = await getDownloadURL(snap.ref);
  return { storagePath, url };
}

function collectResourceDescendants(resourceDocs, rootIds) {
  const byParent = {};
  const byFolder = {};
  const idsToVisit = [...(rootIds || [])];
  const targetIds = new Set(idsToVisit.filter(Boolean));

  resourceDocs.forEach((snap) => {
    const data = snap.data() || {};
    if (data.parentId) {
      if (!byParent[data.parentId]) byParent[data.parentId] = [];
      byParent[data.parentId].push(snap.id);
    }
    if (data.folderId) {
      if (!byFolder[data.folderId]) byFolder[data.folderId] = [];
      byFolder[data.folderId].push(snap.id);
    }
  });

  while (idsToVisit.length > 0) {
    const current = idsToVisit.pop();
    if (!current) continue;

    const children = [...(byParent[current] || []), ...(byFolder[current] || [])];
    children.forEach((id) => {
      if (!targetIds.has(id)) {
        targetIds.add(id);
        idsToVisit.push(id);
      }
    });
  }

  return targetIds;
}

export function createCurriculumService(library) {
  if (!library || !library.id) {
    throw new Error('A curriculum library descriptor with an id is required.');
  }

  const libraryId = library.id;
  const STORAGE_ROOT = library.storageRoot || storageRootForLibrary(libraryId);

  function categoriesCollectionRef() {
    return collection(db, LIBRARIES_COLLECTION, libraryId, 'categories');
  }

  function categoryDocRef(categoryId) {
    return doc(db, LIBRARIES_COLLECTION, libraryId, 'categories', categoryId);
  }

  function textbooksCollectionRef(categoryId) {
    return collection(db, LIBRARIES_COLLECTION, libraryId, 'categories', categoryId, 'textbooks');
  }

  function textbookDocRef(categoryId, textbookId) {
    return doc(db, LIBRARIES_COLLECTION, libraryId, 'categories', categoryId, 'textbooks', textbookId);
  }

  function resourcesCollectionRef(categoryId, textbookId) {
    return collection(
      db,
      LIBRARIES_COLLECTION,
      libraryId,
      'categories',
      categoryId,
      'textbooks',
      textbookId,
      'resources'
    );
  }

  function resourceDocRef(categoryId, textbookId, resourceId) {
    return doc(
      db,
      LIBRARIES_COLLECTION,
      libraryId,
      'categories',
      categoryId,
      'textbooks',
      textbookId,
      'resources',
      resourceId
    );
  }

  async function fetchCategoriesRaw() {
    const categorySnap = await getDocs(categoriesCollectionRef());
    return categorySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  // Ensure a single bucket category exists for `simple` libraries. Returns its id.
  async function ensureDefaultCategory() {
    const categories = sortByOrder(await fetchCategoriesRaw());
    if (categories.length > 0) return categories[0].id;

    const categoryRef = doc(categoriesCollectionRef());
    await setDoc(categoryRef, {
      name: 'Library',
      orderIndex: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return categoryRef.id;
  }

  async function fetchLibrary() {
    const categories = await Promise.all(
      (await fetchCategoriesRaw()).map(async (category) => {
        const textbookSnap = await getDocs(textbooksCollectionRef(category.id));
        const textbooks = sortByOrder(textbookSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        return { ...category, textbooks };
      })
    );
    return sortByOrder(categories);
  }

  // Locate which category holds a textbook (deep links land on /:slug/:textbookId
  // without a category in the URL). Returns { categoryId, textbook } or null.
  async function resolveTextbookLocation(textbookId) {
    const categories = sortByOrder(await fetchCategoriesRaw());
    for (const category of categories) {
      const snap = await getDoc(textbookDocRef(category.id, textbookId));
      if (snap.exists()) {
        return { categoryId: category.id, textbook: { id: snap.id, ...snap.data() } };
      }
    }
    return null;
  }

  async function fetchTextbookDetail(categoryId, textbookId) {
    const textbookSnap = await getDoc(textbookDocRef(categoryId, textbookId));
    if (!textbookSnap.exists()) {
      throw new Error('Textbook not found.');
    }

    const textbook = { id: textbookSnap.id, ...textbookSnap.data() };
    const resourcesSnap = await getDocs(resourcesCollectionRef(categoryId, textbookId));
    const allResources = sortByOrder(resourcesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const resources = allResources.filter((item) => !item.isAnswerKey);
    const answerKeysByParent = {};

    allResources
      .filter((item) => item.isAnswerKey && item.parentId)
      .forEach((keyItem) => {
        if (!answerKeysByParent[keyItem.parentId]) answerKeysByParent[keyItem.parentId] = [];
        answerKeysByParent[keyItem.parentId].push(keyItem);
      });

    Object.keys(answerKeysByParent).forEach((parentId) => {
      answerKeysByParent[parentId] = sortByOrder(answerKeysByParent[parentId]);
    });

    return { textbook, resources, answerKeysByParent };
  }

  async function createCategory({ name, orderIndex }) {
    const categoryRef = doc(categoriesCollectionRef());
    await setDoc(categoryRef, {
      name: name.trim(),
      orderIndex,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return categoryRef.id;
  }

  async function renameCategory({ categoryId, name }) {
    await updateDoc(categoryDocRef(categoryId), {
      name: name.trim(),
      updatedAt: serverTimestamp(),
    });
  }

  async function deleteTextbookInternal(categoryId, textbookId) {
    const textbookSnap = await getDoc(textbookDocRef(categoryId, textbookId));
    if (textbookSnap.exists()) {
      await safeDeleteStorage(textbookSnap.data().coverStoragePath);
    }

    const resourcesSnap = await getDocs(resourcesCollectionRef(categoryId, textbookId));
    for (const resourceSnap of resourcesSnap.docs) {
      await safeDeleteStorage(resourceSnap.data().storagePath);
      await deleteDoc(resourceSnap.ref);
    }

    await deleteDoc(textbookDocRef(categoryId, textbookId));
  }

  async function deleteCategory({ categoryId }) {
    const textbookSnap = await getDocs(textbooksCollectionRef(categoryId));
    for (const textbookDoc of textbookSnap.docs) {
      await deleteTextbookInternal(categoryId, textbookDoc.id);
    }
    await deleteDoc(categoryDocRef(categoryId));
  }

  async function reorderCategories({ categories, fromIndex, direction }) {
    const targetIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (targetIndex < 0 || targetIndex >= categories.length) return;

    const reordered = [...categories];
    [reordered[fromIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[fromIndex]];

    const batch = writeBatch(db);
    reordered.forEach((category, index) => {
      batch.update(categoryDocRef(category.id), {
        orderIndex: index,
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }

  async function createTextbook({ categoryId, title, coverMode, coverFile, coverUrl, orderIndex }) {
    const textbookRef = doc(textbooksCollectionRef(categoryId));
    let coverImageUrl = '';
    let coverStoragePath = '';

    if (coverMode === 'file' && coverFile) {
      const fileName = sanitizeFileName(coverFile.name || 'cover');
      const path = `${STORAGE_ROOT}/${categoryId}/${textbookRef.id}/cover/${makeStorageToken()}_${fileName}`;
      const uploaded = await uploadCurriculumFile(path, coverFile);
      coverImageUrl = uploaded.url;
      coverStoragePath = uploaded.storagePath;
    } else if (coverMode === 'url') {
      coverImageUrl = (coverUrl || '').trim();
    }

    await setDoc(textbookRef, {
      title: title.trim(),
      coverImageUrl,
      coverStoragePath,
      orderIndex,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return textbookRef.id;
  }

  async function updateTextbook({ categoryId, textbookId, title, coverUpdate }) {
    const textbookRef = textbookDocRef(categoryId, textbookId);
    const existingSnap = await getDoc(textbookRef);
    const existing = existingSnap.exists() ? existingSnap.data() : {};

    let nextCoverImageUrl = existing.coverImageUrl || '';
    let nextCoverStoragePath = existing.coverStoragePath || '';

    if (coverUpdate.mode === 'file' && coverUpdate.file) {
      const fileName = sanitizeFileName(coverUpdate.file.name || 'cover');
      const path = `${STORAGE_ROOT}/${categoryId}/${textbookId}/cover/${makeStorageToken()}_${fileName}`;
      const uploaded = await uploadCurriculumFile(path, coverUpdate.file);

      await safeDeleteStorage(nextCoverStoragePath);
      nextCoverImageUrl = uploaded.url;
      nextCoverStoragePath = uploaded.storagePath;
    }

    if (coverUpdate.mode === 'url') {
      await safeDeleteStorage(nextCoverStoragePath);
      nextCoverImageUrl = (coverUpdate.url || '').trim();
      nextCoverStoragePath = '';
    }

    if (coverUpdate.mode === 'remove') {
      await safeDeleteStorage(nextCoverStoragePath);
      nextCoverImageUrl = '';
      nextCoverStoragePath = '';
    }

    await updateDoc(textbookRef, {
      title: title.trim(),
      coverImageUrl: nextCoverImageUrl,
      coverStoragePath: nextCoverStoragePath,
      updatedAt: serverTimestamp(),
    });
  }

  async function deleteTextbook({ categoryId, textbookId }) {
    await deleteTextbookInternal(categoryId, textbookId);
  }

  async function reorderTextbooks({ categoryId, textbooks, fromIndex, direction, toIndex }) {
    const targetIndex =
      Number.isInteger(toIndex) ? toIndex : direction === 'left' ? fromIndex - 1 : fromIndex + 1;

    if (!Number.isInteger(fromIndex) || !Number.isInteger(targetIndex)) return;
    if (targetIndex < 0 || targetIndex >= textbooks.length || fromIndex < 0 || fromIndex >= textbooks.length) return;
    if (targetIndex === fromIndex) return;

    const reordered = [...textbooks];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const batch = writeBatch(db);
    reordered.forEach((textbook, index) => {
      batch.update(textbookDocRef(categoryId, textbook.id), {
        orderIndex: index,
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }

  async function createResource({
    categoryId,
    textbookId,
    title,
    mode,
    linkUrl,
    file,
    isAnswerKey,
    folderId = '',
    parentId = '',
    orderIndex,
  }) {
    const resourceRef = doc(resourcesCollectionRef(categoryId, textbookId));

    let resourceType = mode;
    let url = '';
    let storagePath = '';
    const isSeparator = mode === 'separator';
    const isFolder = mode === 'folder';

    if (isSeparator) {
      resourceType = 'separator';
    } else if (isFolder) {
      resourceType = 'folder';
    } else if (mode === 'file') {
      if (!file) throw new Error('Please choose a file.');
      const fileName = sanitizeFileName(file.name || 'resource');
      const path = `${STORAGE_ROOT}/${categoryId}/${textbookId}/${resourceRef.id}/${makeStorageToken()}_${fileName}`;
      const uploaded = await uploadCurriculumFile(path, file);
      url = uploaded.url;
      storagePath = uploaded.storagePath;
    }

    if (mode === 'link') {
      resourceType = 'link';
      url = (linkUrl || '').trim();
    }

    await setDoc(resourceRef, {
      title: title.trim(),
      type: resourceType,
      url,
      storagePath,
      folderId: folderId || '',
      isAnswerKey: !!isAnswerKey,
      parentId: parentId || '',
      orderIndex,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return resourceRef.id;
  }

  async function updateResource({
    categoryId,
    textbookId,
    resourceId,
    title,
    mode,
    linkUrl,
    file,
    isAnswerKey,
    folderId = '',
    parentId = '',
  }) {
    const refDoc = resourceDocRef(categoryId, textbookId, resourceId);
    const existingSnap = await getDoc(refDoc);
    const existing = existingSnap.exists() ? existingSnap.data() : {};

    let nextType = existing.type || 'link';
    let nextUrl = existing.url || '';
    let nextStoragePath = existing.storagePath || '';

    if (mode === 'separator') {
      await safeDeleteStorage(nextStoragePath);
      nextType = 'separator';
      nextUrl = '';
      nextStoragePath = '';
    } else if (mode === 'folder') {
      await safeDeleteStorage(nextStoragePath);
      nextType = 'folder';
      nextUrl = '';
      nextStoragePath = '';
    } else if (mode === 'file') {
      if (!file) throw new Error('Please choose a file.');
      const fileName = sanitizeFileName(file.name || 'resource');
      const path = `${STORAGE_ROOT}/${categoryId}/${textbookId}/${resourceId}/${makeStorageToken()}_${fileName}`;
      const uploaded = await uploadCurriculumFile(path, file);

      await safeDeleteStorage(nextStoragePath);
      nextType = 'file';
      nextUrl = uploaded.url;
      nextStoragePath = uploaded.storagePath;
    } else if (mode === 'link') {
      await safeDeleteStorage(nextStoragePath);
      nextType = 'link';
      nextUrl = (linkUrl || '').trim();
      nextStoragePath = '';
    }

    // NOTE: `level` is intentionally omitted. The per-resource difficulty level is
    // retired, but the two SAT question banks still carry historical level values.
    // `updateDoc` only writes the listed fields, so leaving `level` out preserves
    // any existing value instead of wiping it when a resource is edited.
    await updateDoc(refDoc, {
      title: title.trim(),
      type: nextType,
      url: nextUrl,
      storagePath: nextStoragePath,
      folderId: folderId || '',
      isAnswerKey: !!isAnswerKey,
      parentId: parentId || '',
      updatedAt: serverTimestamp(),
    });
  }

  async function deleteResource({ categoryId, textbookId, resourceId }) {
    const allSnap = await getDocs(resourcesCollectionRef(categoryId, textbookId));
    const targetIds = collectResourceDescendants(allSnap.docs, [resourceId]);

    for (const snap of allSnap.docs) {
      if (!targetIds.has(snap.id)) continue;
      await safeDeleteStorage(snap.data().storagePath);
      await deleteDoc(snap.ref);
    }
  }

  async function deleteResourcesBulk({ categoryId, textbookId, resourceIds }) {
    const selectedIds = new Set((resourceIds || []).filter(Boolean));
    if (selectedIds.size === 0) return;

    const allSnap = await getDocs(resourcesCollectionRef(categoryId, textbookId));
    const targetIds = collectResourceDescendants(allSnap.docs, [...selectedIds]);

    for (const snap of allSnap.docs) {
      if (!targetIds.has(snap.id)) continue;
      await safeDeleteStorage(snap.data().storagePath);
      await deleteDoc(snap.ref);
    }
  }

  async function moveResourcesToFolder({ categoryId, textbookId, resourceIds, folderId }) {
    const targetFolderId = String(folderId || '').trim();
    if (!targetFolderId) throw new Error('Please choose a folder.');

    const ids = [...new Set((resourceIds || []).filter(Boolean))];
    if (ids.length === 0) return;

    const batch = writeBatch(db);
    ids.forEach((resourceId) => {
      batch.update(resourceDocRef(categoryId, textbookId, resourceId), {
        folderId: targetFolderId,
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }

  async function reorderResources({ categoryId, textbookId, resources, fromIndex, toIndex, direction }) {
    const targetIndex =
      Number.isInteger(toIndex) ? toIndex : direction === 'up' ? fromIndex - 1 : fromIndex + 1;

    if (!Number.isInteger(fromIndex) || !Number.isInteger(targetIndex)) return;
    if (targetIndex < 0 || targetIndex >= resources.length || fromIndex < 0 || fromIndex >= resources.length) return;
    if (targetIndex === fromIndex) return;

    const reordered = [...resources];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const batch = writeBatch(db);
    reordered.forEach((resource, index) => {
      batch.update(resourceDocRef(categoryId, textbookId, resource.id), {
        orderIndex: index,
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }

  return {
    library,
    libraryId,
    storageRoot: STORAGE_ROOT,
    fetchLibrary,
    fetchTextbookDetail,
    resolveTextbookLocation,
    ensureDefaultCategory,
    createCategory,
    renameCategory,
    deleteCategory,
    reorderCategories,
    createTextbook,
    updateTextbook,
    deleteTextbook,
    reorderTextbooks,
    createResource,
    updateResource,
    deleteResource,
    deleteResourcesBulk,
    moveResourcesToFolder,
    reorderResources,
  };
}
